/**
 * zoom-gallery
 *
 * Contents of the PDP zoom modal. Media is split across tabs, and each tab
 * shows the same items either as a grid or as a carousel — the item markup is
 * rendered once and the layout swapped around it, so nothing has to be rebuilt
 * or re-fetched when the view changes.
 *
 * The element is cloned out of a template every time the modal opens, so the
 * state it starts in is read off data attributes set by whatever triggered it.
 */

const GRID = "grid";
const CAROUSEL = "carousel";
const MAX_ZOOM = 3;
// Pointer travel past this is a pan, not a click on the image.
const DRAG_THRESHOLD = 6;

class ZoomGallery extends HTMLElement {
  connectedCallback() {
    if (this.swipers) return;

    this.swipers = new Map();
    this.panels = [...this.querySelectorAll("[data-panel]")];
    this.tabComponent = this.querySelector("tab-component");
    this.activePanel = this.panels[0];

    this.handleClick = (e) => {
      if (e.target.closest("[data-video-control-button]")) return;

      const openItem = e.target.closest("[data-open-item]");
      if (openItem) {
        const item = openItem.closest("[data-item]");
        this._setView(CAROUSEL, this._itemIndex(item));
        return;
      }

      if (e.target.closest("[data-view-all]")) {
        this._setView(GRID);
        return;
      }

      if (this.view === CAROUSEL) this._toggleZoom(e);
    };

    this.handlePointerDown = (e) => {
      this.pointerStart = { x: e.clientX, y: e.clientY };
    };

    this.handleTabSelected = (e) => {
      const panel = e.detail?.panel;
      if (!panel) return;

      this.activePanel = panel;
      this._pauseVideos();

      if (this.view === CAROUSEL) this._openCarousel(panel, 0);
    };

    this.addEventListener("pointerdown", this.handlePointerDown);
    this.addEventListener("click", this.handleClick);
    this.addEventListener("tab-component:tab-selected", this.handleTabSelected);

    // The modal is still display:none while its content is being set, so the
    // carousels can only be measured once it has been shown.
    this.resizeObserver = new ResizeObserver(() => this._updateActiveSwipers());
    this.resizeObserver.observe(this);

    window.loadSwiper.then((swiperLib) => {
      this.swiperLib = swiperLib;
      this._applyInitialState();
    });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this._destroySwipers();
  }

  get view() {
    return this.getAttribute("view");
  }

  /**
   * Opens on whatever the trigger asked for: a specific media item, a specific
   * tab, or the first tab in grid view.
   */
  _applyInitialState() {
    const { initialTab, initialMediaId, initialView } = this.dataset;

    let panel = null;
    let index = 0;

    if (initialMediaId) {
      const item = this.querySelector(
        `[data-item][data-media-id="${initialMediaId}"]`,
      );

      if (item) {
        panel = item.closest("[data-panel]");
        index = this._itemIndex(item);
      }
    }

    if (!panel && initialTab) {
      panel = this.panels.find((el) => el.dataset.tabKey === initialTab);
    }

    panel = panel || this.panels[0];

    if (!panel) return;

    this._selectPanel(panel);
    this._setView(initialView === CAROUSEL ? CAROUSEL : GRID, index);
  }

  /**
   * Slide position of an item. Taken from the DOM rather than an attribute so
   * the tabs don't have to keep a counter in step across their sources.
   */
  _itemIndex(item) {
    const panel = item?.closest("[data-panel]");

    if (!panel) return 0;

    return [...panel.querySelectorAll("[data-item]")].indexOf(item);
  }

  _selectPanel(panel) {
    this.activePanel = panel;

    const tab = this.querySelector(`[role="tab"][aria-controls="${panel.id}"]`);

    if (tab && this.tabComponent?.currentTab !== tab) {
      this.tabComponent?.setSelectedTab(tab, false);
    }
  }

  _setView(view, index = 0) {
    this.setAttribute("view", view);

    if (view === CAROUSEL) {
      this._openCarousel(this.activePanel, index);
    } else {
      this._pauseVideos();
      // Swiper leaves a transform and slide widths behind on the elements the
      // grid then has to lay out, so the carousels go rather than sit hidden.
      this._destroySwipers();
    }
  }

  _openCarousel(panel, index) {
    const swipers = this._panelSwipers(panel);

    if (!swipers) return;

    this._resetZoom(swipers.gallery);
    swipers.thumbs?.update();
    swipers.gallery.update();
    swipers.gallery.slideTo(index, 0);
    this._playCurrentVideo(swipers.gallery);
  }

  _panelSwipers(panel) {
    if (!panel || !this.swiperLib) return null;
    // The dimensions drawing is a layout of its own — no grid, no carousel.
    if (!panel.querySelector("[data-media-container]")) return null;

    if (!this.swipers.has(panel)) {
      this.swipers.set(panel, this._createSwipers(panel));
    }

    return this.swipers.get(panel);
  }

  _createSwipers(panel) {
    const { Swiper } = this.swiperLib;

    panel.querySelectorAll("[data-swiper-class]").forEach((el) => {
      el.classList.add(el.dataset.swiperClass);
    });

    const thumbs = new Swiper(panel.querySelector("[data-thumbnails]"), {
      slidesPerView: "auto",
      spaceBetween: 4,
      breakpoints: { 1024: { spaceBetween: 8 } },
      watchSlidesProgress: true,
      centerInsufficientSlides: true,
      slideToClickedSlide: true,
      navigation: {
        nextEl: panel.querySelector("[data-thumbnails-next]"),
        prevEl: panel.querySelector("[data-thumbnails-previous]"),
      },
    });

    const gallery = new Swiper(panel.querySelector("[data-media-container]"), {
      slidesPerView: 1,
      speed: 400,
      watchSlidesProgress: true,
      zoom: { maxRatio: MAX_ZOOM, minRatio: 1 },
      thumbs: { swiper: thumbs },
      keyboard: { enabled: true },
      navigation: {
        nextEl: panel.querySelector("[data-navigation-next]"),
        prevEl: panel.querySelector("[data-navigation-previous]"),
      },
      on: {
        slideChange: (swiper) => this._playCurrentVideo(swiper),
      },
    });

    return { gallery, thumbs };
  }

  _destroySwipers() {
    this.swipers.forEach(({ gallery, thumbs }, panel) => {
      this._resetZoom(gallery);
      gallery?.destroy(true, true);
      thumbs?.destroy(true, true);

      panel.querySelectorAll("[data-swiper-class]").forEach((el) => {
        el.classList.remove(el.dataset.swiperClass);
      });

      // Zooming out leaves the identity transform behind, and the grid lays
      // out the slide it is on.
      panel
        .querySelectorAll(".swiper-zoom-container, .swiper-zoom-target")
        .forEach((el) => {
          el.style.transform = "";
          el.style.transitionDuration = "";
        });
    });

    this.swipers.clear();
  }

  // Swiper keeps the scale and pan on the elements themselves and leaves them
  // there when it is torn down, so a zoom left open outlives its carousel.
  _resetZoom(gallery) {
    if (!gallery?.zoom || gallery.zoom.scale === 1) return;

    gallery.zoom.out();
  }

  _updateActiveSwipers() {
    if (this.view !== CAROUSEL) return;

    const swipers = this.swipers.get(this.activePanel);

    swipers?.thumbs?.update();
    swipers?.gallery?.update();
  }

  // Swiper only toggles zoom on double click, so single clicks step it manually.
  _toggleZoom(e) {
    if (!e.target.closest(".swiper-zoom-container")) return;

    // Panning a zoomed image ends in a click, which would otherwise step the
    // zoom on release.
    if (this._wasDrag(e)) return;

    const { gallery } = this.swipers.get(this.activePanel) || {};

    if (!gallery) return;

    if (gallery.zoom.scale < MAX_ZOOM) {
      gallery.zoom.in(gallery.zoom.scale + 1);
    } else {
      gallery.zoom.out();
    }
  }

  _wasDrag(e) {
    const start = this.pointerStart;

    if (!start) return false;

    return (
      Math.hypot(e.clientX - start.x, e.clientY - start.y) > DRAG_THRESHOLD
    );
  }

  _playCurrentVideo(swiper) {
    this._pauseVideos();

    const video = swiper.slides[swiper.activeIndex]?.querySelector("video");

    video?.play().catch(() => {});
  }

  _pauseVideos() {
    this.querySelectorAll("video").forEach((video) => video.pause());
  }
}

if (!customElements.get("zoom-gallery")) {
  customElements.define("zoom-gallery", ZoomGallery);
}
